import { Injectable } from '@angular/core';
import { from, Observable, of, Subject } from 'rxjs';
import { HttpService } from '@app/core';
import { NotificationsService } from '@app/shared/services/notifications.service';
import { HubConnection } from '@microsoft/signalr';
import { ActiveMonthEndProcessResponse } from '../models/active-month-end-process-response';
import { MonthEndProcessState } from '../models/month-end-process-state';
import { MonthEndStepTemplate } from '../models/month-end-step-template';
import { MonthEndTaskValue } from '../models/month-end-task-value';
import { MonthEndActionType } from '../models/month-end-action-type.enum';
import { MonthEndTaskModalItem } from '../models/month-end-task-modal-item';
import { MonthEndStartEligibility } from '../models/month-end-start-eligibility';
import { ActiveUser } from '../models/active-user';

const DISCONNECT_THRESHOLD_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class MonthEndService {
  private monthEndProcessHub: HubConnection | null = null;
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionLostEmitted = false;

  readonly connectionLost$ = new Subject<void>();
  readonly reconnected$ = new Subject<void>();

  constructor(
    private http: HttpService,
    private notificationService: NotificationsService,
  ) {}

  connectToSignalRHub(): Observable<void> {
    if (this.monthEndProcessHub) {
      this.monthEndProcessHub.stop();
    }

    this.clearDisconnectTimer();
    this.connectionLostEmitted = false;

    this.monthEndProcessHub = this.notificationService.createConnection(
      'financial-operations/month-end-process-hub',
    );

    this.monthEndProcessHub.serverTimeoutInMilliseconds = 300000;
    this.monthEndProcessHub.keepAliveIntervalInMilliseconds = 150000;

    this.monthEndProcessHub.onreconnecting(() => {
      this.startDisconnectTimer();
    });

    this.monthEndProcessHub.onreconnected(() => {
      this.clearDisconnectTimer();
      this.reconnected$.next();
    });

    this.monthEndProcessHub.onclose(() => {
      this.emitConnectionLost();
    });

    return from(this.monthEndProcessHub.start());
  }

  disconnectFromSignalRHub(): Observable<void> {
    this.clearDisconnectTimer();
    this.connectionLostEmitted = true;
    const hub = this.monthEndProcessHub;
    if (hub) {
      hub.off('ReceiveMonthEndUpdate');
    }
    this.monthEndProcessHub = null;
    return hub ? from(hub.stop()) : of(void 0);
  }

  private startDisconnectTimer(): void {
    if (this.disconnectTimer) {
      return;
    }

    if (this.connectionLostEmitted) {
      return;
    }

    this.disconnectTimer = setTimeout(() => {
      this.emitConnectionLost();
    }, DISCONNECT_THRESHOLD_MS);
  }

  private clearDisconnectTimer(): void {
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
  }

  private emitConnectionLost(): void {
    this.clearDisconnectTimer();
    if (this.connectionLostEmitted) return;
    this.connectionLostEmitted = true;
    this.connectionLost$.next();
  }

  registerSignalRUpdateHandler(
    callback: (processState: MonthEndProcessState) => void,
  ): void {
    if (!this.monthEndProcessHub) return;

    this.monthEndProcessHub.off('ReceiveMonthEndUpdate');
    this.monthEndProcessHub.on('ReceiveMonthEndUpdate', callback);
  }

  isConnected(): boolean {
    return this.monthEndProcessHub?.state === 'Connected';
  }

  getTemplates(glPeriod: number): Observable<MonthEndStepTemplate[]> {
    return this.http.get<MonthEndStepTemplate[]>(
      `financial-operations/monthendprocess/templates/${glPeriod}`,
    );
  }

  getCurrentGlPeriod(): Observable<number> {
    return this.http.get<number>(
      'financial-operations/monthendprocess/gl-period',
    );
  }

  getStartEligibility(): Observable<MonthEndStartEligibility> {
    return this.http.get<MonthEndStartEligibility>(
      'financial-operations/monthendprocess/start-eligibility',
    );
  }

  getActiveMonthEndProcess(): Observable<ActiveMonthEndProcessResponse> {
    return this.http.get<ActiveMonthEndProcessResponse>(
      'financial-operations/monthendprocess',
    );
  }

  getProcessStateByGlPeriod(
    glPeriod: number,
  ): Observable<MonthEndProcessState | null> {
    return this.http.get<MonthEndProcessState | null>(
      `financial-operations/monthendprocess/process-state/${glPeriod}`,
    );
  }

  startMonthEndProcess(): Observable<void> {
    return this.http.post<void>('financial-operations/monthendprocess', {});
  }

  proceedStep(
    processId: number,
    taskValue: MonthEndTaskValue,
  ): Observable<void> {
    return this.http.patch<void>(
      `financial-operations/monthendprocess/${processId}`,
      {
        monthEndProcessId: processId,
        monthEndActionType: MonthEndActionType.Proceed,
        taskValue,
      },
    );
  }

  retryStep(processId: number, taskId: number): Observable<void> {
    return this.http.patch<void>(
      `financial-operations/monthendprocess/${processId}`,
      {
        monthEndProcessId: processId,
        monthEndActionType: MonthEndActionType.Retry,
        taskId,
      },
    );
  }

  completeMonthEnd(processId: number): Observable<void> {
    return this.http.patch<void>(
      `financial-operations/monthendprocess/${processId}`,
      {
        monthEndProcessId: processId,
        monthEndActionType: MonthEndActionType.Complete,
      },
    );
  }

  getModalItems(
    processId: number,
    taskId: number,
  ): Observable<MonthEndTaskModalItem[]> {
    return this.http.get<MonthEndTaskModalItem[]>(
      `financial-operations/monthendprocess/${processId}/tasks/${taskId}/modal-items`,
    );
  }

  getActiveUsers(): Observable<ActiveUser[]> {
    return this.http.get<ActiveUser[]>(
      'financial-operations/monthendprocess/user',
    );
  }

  toggleMultipleUserLockStatus(userIds: number[]): Observable<void> {
    return this.http.patch<void>(
      'financial-operations/monthendprocess/users/toggle-lock-status',
      {
        userIds,
      },
    );
  }
}
